/** Progressive enhancement: content stays readable without JS, observers, or motion. */
export function initializePageMotion(document: Document, view: Window): () => void {
  const elements = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
  if (!elements.length) return () => {};
  const root = document.documentElement;
  const reduced = view.matchMedia?.("(prefers-reduced-motion: reduce)");
  const Observer = (view as Window & { IntersectionObserver?: typeof IntersectionObserver })
    .IntersectionObserver;
  let disposed = false;
  let suspended = false;
  const reveal = (element: HTMLElement, immediate = false): void => {
    if (disposed) return;
    if (immediate) element.classList.add("reveal-without-motion");
    element.classList.add("is-revealed");
    observer?.unobserve(element);
  };
  const observer = Observer
    ? new Observer(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) reveal(entry.target as HTMLElement);
          });
        },
        { threshold: 0.12 },
      )
    : undefined;
  const syncPreference = (): void => {
    if (disposed) return;
    const enabled = Boolean(Observer) && !reduced?.matches;
    root.classList.toggle("page-motion-ready", enabled);
    if (enabled) {
      elements
        .filter((element) => !element.classList.contains("is-revealed"))
        .forEach((element) => observer?.observe(element));
    } else {
      elements.forEach((element) => reveal(element, true));
      observer?.disconnect();
    }
  };
  const revealPassedContent = (): void => {
    if (disposed || !root.classList.contains("page-motion-ready")) return;
    elements.forEach((element) => {
      if (!element.classList.contains("is-revealed") && element.getBoundingClientRect().bottom < 0)
        reveal(element, true);
    });
  };
  const focus = (event: Event): void => {
    elements.forEach((element) => {
      if (event.target && element.contains(event.target as Node)) reveal(element, true);
    });
  };
  const syncVisibility = (): void => {
    root.classList.toggle("page-motion-paused", suspended || document.hidden);
  };
  const suspend = (): void => {
    suspended = true;
    syncVisibility();
  };
  const resume = (): void => {
    suspended = false;
    syncVisibility();
  };

  reduced?.addEventListener("change", syncPreference);
  document.addEventListener("focusin", focus);
  document.addEventListener("visibilitychange", syncVisibility);
  view.addEventListener("scroll", revealPassedContent, { passive: true });
  view.addEventListener("pagehide", suspend);
  view.addEventListener("pageshow", resume);
  syncPreference();
  syncVisibility();

  return () => {
    disposed = true;
    observer?.disconnect();
    reduced?.removeEventListener("change", syncPreference);
    document.removeEventListener("focusin", focus);
    document.removeEventListener("visibilitychange", syncVisibility);
    view.removeEventListener("scroll", revealPassedContent);
    view.removeEventListener("pagehide", suspend);
    view.removeEventListener("pageshow", resume);
    root.classList.remove("page-motion-ready", "page-motion-paused");
    elements.forEach((element) => element.classList.remove("is-revealed", "reveal-without-motion"));
  };
}
